import { all, call, put, takeLatest } from 'redux-saga/effects'
import PlansActions, { PlansTypes } from 'Redux/PlansRedux'

// Fetch all plans
export function* fetchPlans(api, action) {
  try {
    const resp = yield call(api.getPlans)
    if (resp.ok) {
      yield put(PlansActions.plansFetchSuccess(resp.data))
    } else {
      const error = resp.data?.error || 'Failed to fetch plans'
      yield put(PlansActions.plansFetchFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansFetchFailure(error.message || 'Network error'))
  }
}

// Fetch current plan details
export function* fetchCurrentPlan(api, action) {
  try {
    const resp = yield call(api.getCurrentPlan)
    if (resp.ok) {
      yield put(PlansActions.plansCurrentSuccess(resp.data))
    } else {
      const error = resp.data?.error || 'Failed to fetch current plan'
      yield put(PlansActions.plansCurrentFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansCurrentFailure(error.message || 'Network error'))
  }
}

// Upgrade plan
export function* upgradePlan(api, action) {
  try {
    const resp = yield call(api.upgradePlan, { plan: action.planKey })
    if (resp.ok) {
      yield put(PlansActions.plansUpgradeSuccess(resp.data))
      // Refresh plans after successful upgrade
      yield put(PlansActions.plansFetchRequest())
      yield put(PlansActions.plansCurrentRequest())
    } else {
      const error = resp.data?.error || 'Failed to upgrade plan'
      yield put(PlansActions.plansUpgradeFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansUpgradeFailure(error.message || 'Network error'))
  }
}

// Subscribe (get Stripe checkout URL and redirect)
export function* subscribePlan(api, action) {
  try {
    const baseUrl = action.baseUrl || window.location.origin
    const billingCycle = action.billingCycle || 'monthly'
    const resp = yield call(api.subscribePlan, { base_url: baseUrl, billing_cycle: billingCycle })
    if (resp.ok) {
      yield put(PlansActions.plansSubscribeSuccess(resp.data))
      // Redirect to Stripe Checkout
      if (resp.data.checkout_url) {
        window.location.href = resp.data.checkout_url
      }
    } else {
      const error = resp.data?.error || 'Failed to create checkout session'
      yield put(PlansActions.plansSubscribeFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansSubscribeFailure(error.message || 'Network error'))
  }
}

// Downgrade (cancel subscription at period end)
export function* downgradePlan(api, action) {
  try {
    const resp = yield call(api.downgradePlan)
    if (resp.ok) {
      yield put(PlansActions.plansDowngradeSuccess(resp.data))
      // Refresh current plan data
      yield put(PlansActions.plansCurrentRequest())
      yield put(PlansActions.plansFetchRequest())
    } else {
      const error = resp.data?.error || 'Failed to cancel subscription'
      yield put(PlansActions.plansDowngradeFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansDowngradeFailure(error.message || 'Network error'))
  }
}

// Reactivate (undo downgrade)
export function* reactivatePlan(api, action) {
  try {
    const resp = yield call(api.reactivatePlan)
    if (resp.ok) {
      yield put(PlansActions.plansReactivateSuccess(resp.data))
      // Refresh current plan data
      yield put(PlansActions.plansCurrentRequest())
      yield put(PlansActions.plansFetchRequest())
    } else {
      const error = resp.data?.error || 'Failed to reactivate subscription'
      yield put(PlansActions.plansReactivateFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansReactivateFailure(error.message || 'Network error'))
  }
}

// Buy credit top-up
export function* buyCredits(api, action) {
  try {
    const baseUrl = action.baseUrl || window.location.origin
    const resp = yield call(api.buyCredits, { pack_id: action.packId, base_url: baseUrl })
    if (resp.ok) {
      yield put(PlansActions.plansBuyCreditsSuccess(resp.data))
      if (resp.data.checkout_url) {
        window.location.href = resp.data.checkout_url
      }
    } else {
      const error = resp.data?.error || 'Failed to create credit purchase session'
      yield put(PlansActions.plansBuyCreditsFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansBuyCreditsFailure(error.message || 'Network error'))
  }
}

// Compare plans
export function* comparePlans(api, action) {
  try {
    const resp = yield call(api.comparePlans)
    if (resp.ok) {
      yield put(PlansActions.plansCompareSuccess(resp.data))
    } else {
      const error = resp.data?.error || 'Failed to compare plans'
      yield put(PlansActions.plansCompareFailure(error))
    }
  } catch (error) {
    yield put(PlansActions.plansCompareFailure(error.message || 'Network error'))
  }
}

// Watcher saga
export default function* plansSaga(api) {
  yield all([
    takeLatest(PlansTypes.PLANS_FETCH_REQUEST, fetchPlans, api),
    takeLatest(PlansTypes.PLANS_CURRENT_REQUEST, fetchCurrentPlan, api),
    takeLatest(PlansTypes.PLANS_UPGRADE_REQUEST, upgradePlan, api),
    takeLatest(PlansTypes.PLANS_SUBSCRIBE_REQUEST, subscribePlan, api),
    takeLatest(PlansTypes.PLANS_DOWNGRADE_REQUEST, downgradePlan, api),
    takeLatest(PlansTypes.PLANS_REACTIVATE_REQUEST, reactivatePlan, api),
    takeLatest(PlansTypes.PLANS_COMPARE_REQUEST, comparePlans, api),
    takeLatest(PlansTypes.PLANS_BUY_CREDITS_REQUEST, buyCredits, api),
  ])
}
